import {
  ComplianceMetaData,
  Question,
} from '@jupiterone/jupiter-types/question';
import { CloseButton, JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import {
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Field } from 'formik';
import get from 'lodash.get';
import React from 'react';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import AddIcon from 'react-feather/dist/icons/plus';
import FormTextField from './FormTextField';
import QuestionChipInput from './QuestionChipInput';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    addIcon: {
      height: 24,
      strokeWidth: 1.25,
      width: 24,
    },
    addButton: {
      background: theme.palette.primary.main,
      borderRadius: 18,
      border: 'none',
      color: theme.palette.background.paper,
      height: 36,
      minWidth: 36,
      padding: 0,
      pointerEvents: 'all',
      marginLeft: 'auto',
      '&:hover': {
        background: theme.palette.primary.dark,
      },
      marginTop: theme.spacing(1),
    },
    remove: {
      marginLeft: theme.spacing(-1),
    },
    tooltip: {
      background: theme.palette.background.paper,
      color: theme.palette.primary.main,
      padding: theme.spacing(1.5),
    },
    iconButton: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
    },
    controls: {
      marginTop: theme.spacing(2),
    },
    complianceSection: {
      marginTop: theme.spacing(1),
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(),
      marginTop: theme.spacing(-3),
    },
    sectionDivider: {
      backgroundColor: theme.palette.grey[500],
      height: theme.spacing(0.375),
      marginBottom: theme.spacing(2),
    },
    queryNumber: {
      marginRight: theme.spacing(4),
      marginTop: theme.spacing(3),
    },
    removeQueryButtonContainer: {
      marginLeft: theme.spacing(3),
      marginTop: theme.spacing(2),
    },
    queryContainer: {
      display: 'flex',
      marginBottom: theme.spacing(3),
    },
  })
);

interface Props {
  disabled?: boolean;
  onChange: any;
  onChangeChip: (name: string) => (values: any) => void;
  question: Question;
  setFormValues: (question: Question) => void;
  complianceStandards: string[];
}

// Don't delete controls commented code. Waiting on api to be implemented.

const ComplianceFormFields = ({
  disabled,
  onChange,
  onChangeChip,
  question,
  setFormValues,
  complianceStandards,
}: Props) => {
  const classes = useStyles();

  const handleAddCompliance = () => {
    const compliance = question.compliance ? question.compliance : [];
    setFormValues({
      ...question,
      compliance: [
        ...compliance,
        {
          standard: '',
          requirements: [],
        },
      ],
    });
  };

  const handleRemoveCompliance = (index: number) => () => {
    if (question.compliance) {
      question.compliance.splice(index, 1);
      setFormValues({ ...question });
    }
  };

  return (
    <>
      <div className={classes.sectionHeader}>
        <Typography color="inherit">Compliance</Typography>
        <Tooltip
          title="You can optionally map this question to a compliance standard/framework. Select the standard and enter the requirements and/or controls applicable to this question."
          placement="bottom"
          classes={{
            tooltip: classes.tooltip,
          }}
        >
          <IconButton className={classes.iconButton}>
            <HelpIcon size={20} />
          </IconButton>
        </Tooltip>
        {!disabled && (
          <Tooltip title="Add compliance">
            <Button
              id="add-compliance"
              variant="outlined"
              size="small"
              className={classes.addButton}
              onClick={handleAddCompliance}
            >
              <AddIcon className={classes.addIcon} />
            </Button>
          </Tooltip>
        )}
      </div>

      <Divider className={classes.sectionDivider} />

      {(question.compliance || []).map(
        (compliance: ComplianceMetaData, index: number) => {
          const standard = `compliance.${index}.standard`;
          const requirements = `compliance.${index}.requirements`;

          return (
            <div key={index} className={classes.queryContainer}>
              <Typography variant="h5" className={classes.queryNumber}>
                {index + 1}
              </Typography>

              <Grid container spacing={0}>
                <Grid item xs={12} className={classes.complianceSection}>
                  <Field key={standard} name={standard}>
                    {({ field, form }: any) => (
                      <FormTextField
                        select={true}
                        name={field.name}
                        value={field.value}
                        fullWidth={true}
                        label="Standard"
                        disabled={disabled}
                        onChange={onChange}
                        errorText={
                          get(form.touched, field.name) &&
                          get(form.errors, field.name)
                        }
                      >
                        {complianceStandards.map((standard) => (
                          <MenuItem key={standard} value={standard}>
                            {standard}
                          </MenuItem>
                        ))}
                        {complianceStandards.indexOf(
                          compliance?.standard || ''
                        ) === -1 && (
                          <MenuItem
                            key={compliance.standard}
                            value={compliance.standard}
                          >
                            {compliance.standard}
                          </MenuItem>
                        )}
                      </FormTextField>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <QuestionChipInput
                    label="Requirement/Control Mappings"
                    defaultValue={[
                      ...((question.compliance?.[index]
                        ?.requirements as string[]) ?? []),
                      ...((question.compliance?.[index]
                        ?.controls as string[]) ?? []),
                    ]}
                    disabled={disabled}
                    fullWidth={true}
                    onChange={onChangeChip(requirements)}
                  />
                </Grid>
              </Grid>
              {!disabled && (
                <div className={classes.removeQueryButtonContainer}>
                  <CloseButton
                    className={classes.remove}
                    label="Remove compliance"
                    size={20}
                    onClose={handleRemoveCompliance(index)}
                  />
                </div>
              )}
            </div>
          );
        }
      )}
    </>
  );
};

export default ComplianceFormFields;
